<template>
  <div class="detail-table-container mb-4">
    <table class="table detail-table">
      <tbody>
        <tr>
          <td class="detail-label pt-0">Subject</td>
          <td class="detail-separator p-0">:</td>
          <td class="detail-value pt-0">
            {{ data.subject }}
          </td>
        </tr>
        <tr>
          <td class="detail-label pt-0">Priority</td>
          <td class="detail-separator p-0">:</td>
          <td class="detail-value pt-0">
            {{
              [
                { value: 1, text: 'Low' },
                { value: 2, text: 'Medium' },
                { value: 3, text: 'High' },
                { value: 5, text: 'Normal (Default)' },
              ].find((el) =>
                el.value === data.priority ? Number(data.priority) : 5
              )?.text
            }}
          </td>
        </tr>
        <tr>
          <td class="detail-label pt-0">Company Name</td>
          <td class="detail-separator p-0">:</td>
          <td class="detail-value pt-0">
            {{
              optionsCompanies.find((el) => el.value === data.company_id)
                ?.text || '-'
            }}
          </td>
        </tr>
        <tr>
          <td class="detail-label">Fleet Ownership</td>
          <td class="detail-separator">:</td>
          <td class="detail-value">
            {{
              fleetOwnershipOptions.find(
                (el) => el.value === data.fleet_ownership
              )?.text || '-'
            }}
          </td>
        </tr>
        <tr>
          <td class="detail-label">Company Third Party</td>
          <td class="detail-separator">:</td>
          <td class="detail-value">
            {{ data.third_party_company || '-' }}
          </td>
        </tr>
        <tr>
          <td class="detail-label">Fleet Type</td>
          <td class="detail-separator">:</td>
          <td class="detail-value">
            <div class="fleet-types-container">
              <template
                v-if="selectedFleetTypes && selectedFleetTypes.length > 0"
              >
                <b-badge
                  v-for="(fleet, index) in selectedFleetTypes"
                  :key="index"
                  variant="light"
                  class="fleet-type-badge"
                >
                  {{ fleet?.text || fleet?.name || '-' }}
                </b-badge>
              </template>
              <template v-else>-</template>
            </div>
          </td>
        </tr>
        <tr>
          <td class="detail-label">Period</td>
          <td class="detail-separator">:</td>
          <td class="detail-value">
            {{ formatDateRange(data.periode_start, data.periode_end) }}
          </td>
        </tr>
        <tr>
          <td class="detail-label">Shipment Plan Number</td>
          <td class="detail-separator">:</td>
          <td class="detail-value">{{ data.shipment_plan_number || '-' }}</td>
        </tr>
        <tr>
          <td class="detail-label">Contract Type</td>
          <td class="detail-separator">:</td>
          <td class="detail-value">
            {{ data?.contract_type?.name || '-' }}
            <template v-if="data?.contract_type?.is_spal === 1">
              <b-badge variant="info" class="ml-2">
                <i class="fa fa-info-circle"></i>
                This type using
                <strong>SPAL</strong>
              </b-badge>
            </template>
          </td>
        </tr>
        <tr>
          <td class="detail-label">Term</td>
          <td class="detail-separator">:</td>
          <td class="detail-value">
            {{ data?.terms?.name || '-' }}
          </td>
        </tr>
        <tr>
          <td class="detail-label">Port of Loading</td>
          <td class="detail-separator">:</td>
          <td class="detail-value">
            {{ data.pol?.length ? data.pol[0].value : '-' }}
          </td>
        </tr>
        <tr>
          <td class="detail-label">Port of Discharging</td>
          <td class="detail-separator">:</td>
          <td class="detail-value">
            {{ data.pod?.length ? data.pod[0].value : '-' }}
          </td>
        </tr>
        <tr>
          <td class="detail-label">Distance</td>
          <td class="detail-separator">:</td>
          <td class="detail-value">
            {{ data.distance ? `${data.distance} NM` : '-' }}
          </td>
        </tr>
        <tr>
          <td class="detail-label">Laycan</td>
          <td class="detail-separator">:</td>
          <td class="detail-value">
            {{ formatDateRange(data.laycan_start, data.laycan_end) }}
          </td>
        </tr>
        <tr>
          <td class="detail-label">Cargo</td>
          <td class="detail-separator">:</td>
          <td class="detail-value">
            {{
              optionsCargoType.find((el) => el.value === data.cargo_type_id)
                ?.text || '-'
            }}
            - {{ formatCurrency(totalQuantity) }}
            {{ optionUnit.find((el) => el.value === data.unit)?.text || '' }}
          </td>
        </tr>
        <tr>
          <td class="detail-label">Freight Rate</td>
          <td class="detail-separator">:</td>
          <td class="detail-value">
            IDR {{ formatCurrency(data.freight_rate_est) }}
            <template v-if="data.include_ppn">
              <b-form-checkbox
                v-model="data.include_ppn"
                disabled
                inline
                class="ml-2"
              >
                Include PPN
              </b-form-checkbox>
            </template>
          </td>
        </tr>
        <tr>
          <td class="detail-label">Remarks</td>
          <td class="detail-separator">:</td>
          <td class="detail-value">
            {{
              data.remarks ||
              'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.'
            }}
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import moment from 'moment'

export default {
  name: 'DetailProjectPlan',
  props: {
    data: {
      type: Object,
      required: true,
      default: () => ({}),
    },
    optionsCompanies: {
      type: Array,
      default: () => [],
    },
    fleetOwnershipOptions: {
      type: Array,
      default: () => [],
    },
    fleetTypeOptions: {
      type: Array,
      default: () => [],
    },
    contractTypeOptions: {
      type: Array,
      default: () => [],
    },
    termOptions: {
      type: Array,
      default: () => [],
    },
    optionsCargoType: {
      type: Array,
      default: () => [],
    },
    optionUnit: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    selectedContractType() {
      return this.contractTypeOptions.find(
        (el) =>
          el.id === this.data.contract_type_id ||
          el.value === this.data.contract_type_id
      )
    },
    selectedFleetTypes() {
      if (
        !this.data.fleet_type_ids ||
        !Array.isArray(this.data.fleet_type_ids)
      ) {
        return []
      }
      return this.data.fleet_type_ids
        .map((typeId) => {
          const option =
            this.fleetTypeOptions.find((option) => option.value === typeId) ||
            this.fleetTypeOptions.find((option) => option.id === typeId)
          return option
            ? {
                value: option.value || option.id,
                text: option.text || option.name,
              }
            : null
        })
        .filter(Boolean)
    },
    totalQuantity() {
      if (this.data.detail_plan && Array.isArray(this.data.detail_plan)) {
        return this.data.detail_plan.reduce(
          (total, item) => total + Number(item.plan_quantity || 0),
          0
        )
      }
      return this.data.total_quantity || 0
    },
  },
  methods: {
    formatCurrency(value) {
      if (!value) return '0'
      return new Intl.NumberFormat('id-ID').format(value)
    },

    formatDateRange(startDate, endDate) {
      if (!startDate && !endDate) return '-'
      if (!startDate) return moment(endDate).format('DD/MM/YYYY')
      if (!endDate) return moment(startDate).format('DD/MM/YYYY')
      return `${moment(startDate).format('DD/MM/YYYY')} - ${moment(
        endDate
      ).format('DD/MM/YYYY')}`
    },
  },
}
</script>

<style scoped>
.detail-table-container {
  border-radius: 8px;
  overflow: hidden;
}

.detail-table {
  margin-bottom: 0;
  background: white;
  border: none;
  border-collapse: separate;
  border-spacing: 0;
}

.detail-table td {
  padding: 12px 16px;
  border: none !important;
  border-top: none !important;
  border-bottom: none !important;
  vertical-align: middle;
}

.detail-table tr {
  border: none !important;
  border-top: none !important;
  border-bottom: none !important;
}

.detail-table tbody tr {
  border: none !important;
}

.detail-table tr:last-child td {
  border-bottom: none !important;
}

.detail-label {
  padding-left: 0px !important;
  color: #495057;
  width: 35%;
}

.detail-separator {
  width: 1%;
  text-align: center;
  font-weight: 600;
}

.detail-value {
  font-weight: 600;
  color: #212529;
  width: 77%;
}

.fleet-types-container .fleet-type-badge {
  margin-right: 8px;
  margin-bottom: 4px;
  border: 1px solid #6c757d;
  color: #495057;
}

.detail-value .b-form-checkbox {
  margin-bottom: 0;
}

.detail-value .b-form-checkbox .custom-control-label {
  font-size: 0.875rem;
}
</style>
